import React, { Component } from "react";
import { connect } from "react-redux";
import { Container, Progress, Header } from "semantic-ui-react";
import { useNavigate } from "react-router-dom";
import { fetchExams } from "../reducers/exam";
import SelectExam from "../components/Bookings/SelectExam";
import SelectDates from "../components/Bookings/SelectDates";
import PaymentDetails from "../components/Bookings/PaymentDetails";
import ConfirmBooking from "../components/Bookings/ConfirmBooking";
import Receipt from "../components/Bookings/Receipt";
import { withTranslation } from "react-i18next";

class BookExam extends Component {
  constructor(props) {
    super(props);
    this.nextPage = this.nextPage.bind(this);
    this.previousPage = this.previousPage.bind(this);

    this.state = {
      page: 1,
      percentage: 0,
      formData: {},
    };
  }

  componentDidMount() {
    this.checkProfileImageAndInstitution();
  }

  checkProfileImageAndInstitution = () => {
    // Check if learning institution is selected
    if (!this.props.kc.tokenParsed.learning_institution) {
      this.props.navigate("/account-details");
      return;
    }

    const imageUrl = 
      `${process.env.REACT_APP_API_ADDRESS}/api/images/profile/` +
      `${this.props.kc.tokenParsed.sub}_user_profile_img.png?token=${
        this.props.kc.token
      }&cb=${Math.round(new Date().getTime() / 1000)}`;

    const img = new Image();
    
    img.addEventListener("load", () => {
      // Profile image exists, allow access to booking
    });
    
    img.addEventListener("error", () => {
      this.props.navigate("/account-details");
    });
    
    img.src = imageUrl;
  };
  nextPage(formData) {
    console.debug("Navigating to next page", { currentPage: this.state.page, formData });

    const updatedFormData = {
      ...this.state.formData,
      ...formData,
    };

    // Determine next page - skip payment page if not required
    let nextPage = this.state.page + 1;
    if (this.state.page === 2 && updatedFormData.exam && !updatedFormData.exam.requires_payment) {
      nextPage = 4; // Skip payment page (page 3)
    }

    // Calculate percentage based on actual step progress
    const requiresPayment = updatedFormData.exam?.requires_payment ?? true;
    const totalSteps = requiresPayment ? 4 : 3;
    let currentStepNum;
    if (nextPage === 1) currentStepNum = 1;
    else if (nextPage === 2) currentStepNum = 2;
    else if (nextPage === 3) currentStepNum = 3;
    else if (nextPage === 4) currentStepNum = requiresPayment ? 4 : 3;
    else currentStepNum = totalSteps;

    const percentage = (currentStepNum / totalSteps) * 100;

    this.setState({
      page: nextPage,
      percentage: percentage,
      formData: updatedFormData,
    });
  }

  previousPage(formData) {
    const updatedFormData = {
      ...this.state.formData,
      ...formData,
    };

    // Determine previous page - skip payment page if not required
    let previousPage = this.state.page - 1;
    if (this.state.page === 4 && !updatedFormData.exam?.requires_payment) {
      previousPage = 2; // Skip payment page (page 3)
    }

    // Calculate percentage based on actual step progress
    const requiresPayment = updatedFormData.exam?.requires_payment ?? true;
    const totalSteps = requiresPayment ? 4 : 3;
    let currentStepNum;
    if (previousPage === 1) currentStepNum = 1;
    else if (previousPage === 2) currentStepNum = 2;
    else if (previousPage === 3) currentStepNum = 3;
    else if (previousPage === 4) currentStepNum = requiresPayment ? 4 : 3;
    else currentStepNum = 0;

    const percentage = (currentStepNum / totalSteps) * 100;

    console.debug("Navigating to previous page", { currentPage: this.state.page, formData });
    this.setState({
      page: previousPage,
      percentage: percentage,
      formData: updatedFormData,
    });
  }

  showStep = () => {
    console.log("Rendering step:", this.state.page);
    switch (this.state.page) {
      case 1:
        return (
          <SelectExam
            onForward={this.nextPage}
            formData={this.state.formData}
            step={this.state.page}
          />
        );
      case 2:
        return (
          <SelectDates
            onBackward={this.previousPage}
            onForward={this.nextPage}
            formData={this.state.formData}
            step={this.state.page}
          />
        );
      case 3:
        return (
          <PaymentDetails
            onBackward={this.previousPage}
            onForward={this.nextPage}
            formData={this.state.formData}
            step={this.state.page}
          />
        );
      case 4:
        return (
          <ConfirmBooking
            onBackward={this.previousPage}
            onForward={this.nextPage}
            formData={this.state.formData}
            step={this.state.page}
          />
        );
      case 5:
        return (
          <Receipt
            onBackward={this.previousPage}
            onForward={this.nextPage}
            formData={this.state.formData}
            step={this.state.page}
          />
        );
      default:
        console.error("Unknown step, defaulting to SelectExam:", this.state.page);
        return (
          <SelectExam
            onForward={this.nextPage}
            formData={this.state.formData}
            step={this.state.page}
          />
        );
    }
  };

  getStepLabel = () => {
    switch (this.state.page) {
      case 1:
        return this.props.t("BookExam.step1Label", "Select Exam");
      case 2:
        return this.props.t("BookExam.step2Label", "Select Date & Time");
      case 3:
        return this.props.t("BookExam.step3Label", "Payment Details");
      case 4:
        return this.props.t("BookExam.step4Label", "Confirm Booking");
      case 5:
        return this.props.t("BookExam.step5Label", "Confirmation");
      default:
        return "";
    }
  };

  render() {
    const stepLabel = this.getStepLabel();

    // Calculate total steps and current step based on whether payment is required
    const requiresPayment = this.state.formData.exam?.requires_payment ?? true;
    const totalSteps = requiresPayment ? 4 : 3;

    // Map page number to step number (accounting for skipped payment page)
    let currentStep;
    if (this.state.page === 1) currentStep = 1;
    else if (this.state.page === 2) currentStep = 2;
    else if (this.state.page === 3) currentStep = 3;
    else if (this.state.page === 4) currentStep = requiresPayment ? 4 : 3;
    else currentStep = totalSteps; // Page 5 (confirmation) shows as final step

    // Determine progress bar color based on current step
    let progressColor;
    if (this.state.page === 5) {
      progressColor = null; // success prop handles this
    } else if (this.state.page <= 2) {
      progressColor = "blue"; // Early steps
    } else {
      progressColor = "teal"; // Later steps (payment/confirm)
    }

    return (
      <Container>
        <div style={{ marginBottom: "2em", marginTop: "1em" }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "0.5em"
          }}>
            <span style={{ fontWeight: "600", fontSize: "1.1em" }}>
              {stepLabel}
            </span>
            <span style={{ color: "#666", fontSize: "0.9em" }}>
              {this.state.page < 5 && this.props.t("BookExam.stepOf", {
                current: currentStep,
                total: totalSteps,
              })}
            </span>
          </div>
          <Progress
            percent={this.state.percentage}
            color={progressColor}
            success={this.state.page === 5}
            active={this.state.page < 5}
            size="small"
          />
          {this.state.page < 5 && (
            <div style={{
              marginTop: "0.5em",
              fontSize: "0.85em",
              color: "#666",
              fontStyle: "italic"
            }}>
              {this.props.t("Emails.BookingRequest.approvalTimeFrame")}
            </div>
          )}
        </div>
        {this.showStep()}
      </Container>
    );
  }
}

export default withTranslation()(
  connect(
    (state) => ({
      kc: state.keycloak,
      exam: state.exam,
    }),
    {
      fetchExams,
    }
  )((props) => {
    const navigate = useNavigate();
    return <BookExam {...props} navigate={navigate} />;
  })
);
